import { Slider } from "@mui/material";
import { Mark } from "@mui/core";
import { makeStyles } from "@mui/styles";
import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Goals } from "../../models/Goals";
import { Macronutrient, MacroNutrientRatio } from "../../models/MacroNutrient";
import { Util } from "../../models/Util";
import "../MacroNutrient/MacroNutrients.scss";
import { GraphMode } from "../MacroNutrient/MacroNutrients";
import { AnalyticsApi } from "../../api/user";

interface MacroNutrientCustomProps {
	goal: Goals;
	valueSelected(response?: any): void;
}

function valuetext(value: any) {
	return `${value}°C`;
}

const marks = [
	{
		value: 20,
		label: "20%",
	},
	{
		value: 50,
		label: "50%",
	},
];
const useStyles = makeStyles((theme) => ({
	root: {
		width: 300,
	},
	rail: {
		backgroundImage: "linear-gradient(.25turn, gray 50%, 0, black)",
		color: "black",
		opacitiy: 0.8,
	},
	thumbProtein: {
		color: "#03bce4",
		"&~&": {
			color: "#03bce4",
		},
	},
	trackProtein: {
		color: "#03bce4",
	},

	thumbCarb: {
		color: "#f4a238",
		"&~&": {
			color: "#f4a238",
		},
	},
	trackCarb: {
		background: "#f4a238",
		color: "#f4a238",
	},

	thumbFat: {
		color: "#ffe36a",
		"&~&": {
			color: "#ffe36a",
		},
	},
	trackFat: {
		background: "#ffe36a",
		color: "#ffe36a",
	},
}));

const MacroNutrientCustom: React.FC<MacroNutrientCustomProps> = ({ goal, valueSelected }): JSX.Element => {
	const classes = useStyles();
	const [mode, setMode] = React.useState(GraphMode.option);
	const [enabled, setEnabled] = React.useState(false);
	const [macroRatios, setMacroRatios] = React.useState(Util.getMacroNutrientSuggestion(goal.eatingStyle!));
	const [customMacroRatios, setCustomMacroRatios] = React.useState(Util.getMacroNutrientSuggestion(goal.eatingStyle!)[0]);
	const [selectedLabel, setSelectedLabel] = React.useState("");
	const [proteinValue, setProteinValue] = React.useState(customMacroRatios.protein);
	const [carbValue, setCarbValue] = React.useState(customMacroRatios.carbs);
	const [fatValue, setFatValue] = React.useState(customMacroRatios.fats);

	const [ranges, setRanges] = React.useState({
		protein: Util.getProteinRange(goal.eatingStyle!),
		carb: Util.getCarbRange(goal.eatingStyle!),
		fat: Util.getFatRange(goal.eatingStyle!),
	});

	const getMarks = (value: string): Mark[] => {
		let markValue;
		switch (value) {
			case "protein":
				markValue = ranges["protein"];
				break;
			case "carb":
				markValue = ranges["carb"];
				break;
			case "fat":
				markValue = ranges["fat"];
				break;
		}
		const markArr = [
			{
				value: markValue.min,
				label: `${markValue.min}%`,
			},
			{
				value: markValue.max,
				label: `${markValue.max}%`,
			},
		];

		return markArr;
	};

	const handleProteinChange = (event: any, newValue: any) => {
		if (newValue < ranges["protein"].min || newValue > ranges["protein"].max) {
			return;
		}
		let customRatio: MacroNutrientRatio = {
			protein: newValue,
			carbs: customMacroRatios.carbs,
			fats: customMacroRatios.fats,
		};
		validateMacroValues(customRatio, 'protein');
		setMacroRatio(customRatio);
	};

	const handleCarbChange = (event: any, newValue: any) => {
		if (newValue < ranges["carb"].min || newValue > ranges["carb"].max) {
			return;
		}
		let customRatio: MacroNutrientRatio = {
			protein: customMacroRatios.protein,
			carbs: newValue,
			fats: customMacroRatios.fats,
		};
		validateMacroValues(customRatio, 'carbs');
		setMacroRatio(customRatio);
	};

	const handleFatChange = (event: any, newValue: any) => {
		if (newValue < ranges["fat"].min || newValue > ranges["fat"].max) {
			return;
		}
		let customRatio: MacroNutrientRatio = {
			protein: customMacroRatios.protein,
			carbs: customMacroRatios.carbs,
			fats: newValue,
		};

		validateMacroValues(customRatio, 'fat');
		setMacroRatio(customRatio);
	};

	const setMacroRatio = (customRatio: MacroNutrientRatio) => {
		setCustomMacroRatios(customRatio);
		setCarbValue(customRatio.carbs);
		setProteinValue(customRatio.protein);
		setFatValue(customRatio.fats);
	}

	const validateMacroValues = (customRatio: MacroNutrientRatio, type: string) => {
		switch (type) {
			case 'fat': {
				customRatio.carbs = Math.floor(getMacroValues(customRatio.protein, customRatio.fats, ranges['carb']));
				customRatio.protein = Math.floor(getMacroValues(customRatio.fats, customRatio.carbs, ranges['protein']));
				break;
			}
			case 'protein': {
				customRatio.carbs = Math.floor(getMacroValues(customRatio.protein, customRatio.fats, ranges['carb']));
				customRatio.fats = Math.floor(getMacroValues(customRatio.protein, customRatio.carbs, ranges['fat']));

				break;
			}

			case 'carbs': {
				customRatio.fats = Math.floor(getMacroValues(customRatio.protein, customRatio.carbs, ranges['fat']));
				customRatio.protein = Math.floor(getMacroValues(customRatio.fats, customRatio.carbs, ranges['protein']));
				break;
			}
		}
	}

	const getMacroValues = (macro1: number, macro2: number, range): number => {
		let macroValue = 100 - (macro1 + macro2);

		if (macroValue > range.max) {
			macroValue = range.max;
		} else if (macroValue < range.min) {
			macroValue = range.min;
		}

		return macroValue;
	}


	const valueClicked = () => {
		AnalyticsApi.logEvent('Custom Macronutrient Selected');
		if (mode === GraphMode.customize) {
			goal.macroNutrient = new Macronutrient(customMacroRatios, GraphMode.customize);
		}
		valueSelected();
	};

	const optionSelected = (label: string, index: number) => {
		setSelectedLabel(label);
		setEnabled(true);
		goal.macroNutrient = new Macronutrient(macroRatios[index], GraphMode.option);
	};

	const customise = () => {
		setMode(GraphMode.option);
	};

	const setOwn = () => {
		setMode(GraphMode.customize);
	};

	React.useEffect(() => {
		if (typeof goal.macroNutrient !== 'undefined') {
			if (goal.macroNutrient.graphMode !== GraphMode.standard) {
				setMode(goal.macroNutrient.graphMode);
			}

			if (goal.macroNutrient.graphMode === GraphMode.customize) {
				setCustomMacroRatios(goal.macroNutrient.macroRatio);
				setProteinValue(goal.macroNutrient.macroRatio.protein);
				setCarbValue(goal.macroNutrient.macroRatio.carbs);
				setFatValue(goal.macroNutrient.macroRatio.fats);
			} else {
				setSelectedLabel(goal.macroNutrient.macroRatio!.label!);
			}
		}
	}, []);

	const renderGraph = () => {
		if (mode === GraphMode.option) {
			return (
				<>
					<div className="selector">
						<div className="selector-btn selector-btn-ative-left" onClick={(e) => customise()}>
							Recomendation
						</div>
						<div className="selector-btn" onClick={(e) => setOwn()}>
							Enter My Own
						</div>
					</div>
					<div className="graph-group">
						{macroRatios.map((macroRatio, i) => {
							const graphState = JSON.parse(JSON.stringify(Util.graphState));
							graphState.datasets[0].data = [macroRatio.protein, macroRatio.carbs, macroRatio.fats];
							return (
								<div
									key={i}
									className={
										selectedLabel === macroRatio.label ? "graph-container selected" : "graph-container"
									}
									onClick={(e) => optionSelected(macroRatio.label!, i)}
								>
									<Doughnut data={graphState} options={{}} />
									<div className="macro-label">{macroRatio.label}</div>
									<div>
										<span className="bubble blue"></span> ~{macroRatio.protein}% Protein
										<span className="bubble orange"></span> ~{macroRatio.carbs}% Carbs
										<span className="bubble yellow"></span> ~{macroRatio.fats}% Fats
									</div>
								</div>
							);
						})}
					</div>
				</>
			);
		} else if (mode === GraphMode.customize) {
			return (
				<>
					<div className="selector">
						<div className="selector-btn" onClick={(e) => customise()}>
							Recomendation
						</div>
						<div className="selector-btn selector-btn-ative-right" onClick={(e) => setOwn()}>
							Enter My Own
						</div>
					</div>

					<div className="slider-container">
						<div className="field-blue slider-text">Protein</div>
						<Slider
							getAriaLabel={() => "Temperature range"}
							value={proteinValue}
							onChange={handleProteinChange}
							valueLabelDisplay="auto"
							getAriaValueText={valuetext}
							marks={getMarks("protein")}
							classes={{
								thumb: classes.thumbProtein,
								rail: classes.rail,
								track: classes.trackProtein,
							}}
						/>

						<div className="field-orange slider-text">Carb</div>

						<Slider
							getAriaLabel={() => "Temperature range"}
							value={carbValue}
							onChange={handleCarbChange}
							valueLabelDisplay="auto"
							getAriaValueText={valuetext}
							marks={getMarks("carb")}
							classes={{
								thumb: classes.thumbCarb,
								rail: classes.rail,
								track: classes.trackCarb,
							}}
						/>

						<div className="field-yellow slider-text">Fat</div>
						<Slider
							getAriaLabel={() => "Temperature range"}
							value={fatValue}
							onChange={handleFatChange}
							valueLabelDisplay="auto"
							getAriaValueText={valuetext}
							marks={getMarks("fat")}
							classes={{
								thumb: classes.thumbFat,
								rail: classes.rail,
								track: classes.trackFat,
							}}
						/>
						<div>
							The adjustable macronutrient ranges are based on your chosen eating style:{" "}
							<b>{goal.getEatingStyle()}</b>
						</div>
					</div>
				</>
			);
		}
	};

	const renderButtons = () => {
		if (mode === GraphMode.option) {
			return (
				<div className="">
					{enabled ? (
						<button className="greenButton" onClick={(e) => valueClicked()}>
							Next
						</button>
					) : (
						<button className="lightGrayButton">Next</button>
					)}
				</div>
			);
		} else if (mode === GraphMode.customize) {
			return (
				<div className="">
					<button className="greenButton" onClick={(e) => valueClicked()}>
						Next
					</button>
				</div>
			);
		}
	};
	return (
		<div className="content">
			{renderGraph()}
			{renderButtons()}
		</div>
	);
};

export default MacroNutrientCustom;
